import { Label } from '@/components/ui/label';
import { CurrencyInput } from '@/components/ui/currency-input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { SubmitDealStepProps } from '@/types/submit-deal.types';
import { useLanguage } from '@/contexts/LanguageContext';
import { useEffect } from 'react';
import { Building2 } from 'lucide-react';

function fmt(n: number, currency = 'CAD') {
  return new Intl.NumberFormat('fr-CA', { style: 'currency', currency, minimumFractionDigits: 0 }).format(n || 0);
}

export const StepFinancials = ({ formData, updateFormData, errors, clearError, t }: SubmitDealStepProps) => {
  const { language } = useLanguage();
  const isFr = language === 'fr';
  const currency = formData.general?.currency || 'CAD';

  const handleChange = (field: string, rawValue: string) => {
    const value = rawValue === '' ? '' : parseInt(rawValue);
    updateFormData('financials', { [field]: value });
    clearError(field);
  };

  // AJOUTÉ : total immobilier calculé à l'étape 5 (somme des propriétés
  // possédées) — realEstateAskPrice est déjà synchronisé automatiquement
  // par StepRealEstate.tsx à chaque changement du répéteur de propriétés.
  const realEstateTotal = formData.realEstate?.realEstateAskPrice || 0;
  const askingPriceNum = Number(formData.financials.askingPrice) || 0;
  const includeRealEstate = !!formData.financials.includeRealEstateInPrice;
  const combinedTotal = includeRealEstate ? askingPriceNum + realEstateTotal : askingPriceNum;

  // Garde le total combiné synchronisé automatiquement dans le formData
  // dès que le prix, le choix Oui/Non, ou le total immobilier change —
  // pour que les autres écrans (CIM, DSCR) puissent l'utiliser sans
  // recalculer eux-mêmes cette logique.
  useEffect(() => {
    updateFormData('financials', { totalAskingPriceWithRealEstate: combinedTotal });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askingPriceNum, includeRealEstate, realEstateTotal]);

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="askingPrice">{t.step6.askingPrice} *</Label>
        <CurrencyInput
          id="askingPrice"
          min={0}
          value={formData.financials.askingPrice || ''}
          onChange={(v) => handleChange('askingPrice', v)}
          className={errors.askingPrice ? 'border-destructive' : ''}
        />
        <p className="text-xs text-muted-foreground">{t.step6.askingPriceHint}</p>
        {errors.askingPrice && <p className="text-sm text-destructive">{errors.askingPrice}</p>}
      </div>

      {/* AJOUTÉ : Inclure l'immobilier dans le prix demandé, avec calcul
          automatique du total combiné à partir des propriétés saisies à
          l'étape 5 (Immobilier). */}
      <div className="space-y-3 p-4 rounded-md border border-border bg-muted/30">
        <Label className="flex items-center gap-2">
          <Building2 className="h-4 w-4" />
          {isFr ? "Inclure l'immobilier dans le prix demandé?" : 'Include real estate in the asking price?'}
        </Label>
        <RadioGroup
          value={includeRealEstate ? 'yes' : 'no'}
          onValueChange={(value) => updateFormData('financials', { includeRealEstateInPrice: value === 'yes' })}
          className="flex space-x-6"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="yes" id="includeRealEstate-yes" />
            <Label htmlFor="includeRealEstate-yes" className="font-normal">{isFr ? 'Oui' : 'Yes'}</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="no" id="includeRealEstate-no" />
            <Label htmlFor="includeRealEstate-no" className="font-normal">{isFr ? 'Non' : 'No'}</Label>
          </div>
        </RadioGroup>

        {includeRealEstate && (
          <div className="mt-2 pt-3 border-t border-border space-y-1 text-sm">
            {realEstateTotal <= 0 ? (
              <p className="text-amber-600">
                {isFr
                  ? "Aucune valeur immobilière trouvée à l'étape 5 — ajoutez au moins une propriété possédée pour que ce calcul fonctionne."
                  : 'No real estate value found from Step 5 — add at least one owned property for this calculation to work.'}
              </p>
            ) : (
              <>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{isFr ? "Prix de l'entreprise" : 'Business Price'}</span>
                  <span>{fmt(askingPriceNum, currency)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{isFr ? 'Immobilier (étape 5)' : 'Real Estate (Step 5)'}</span>
                  <span>+ {fmt(realEstateTotal, currency)}</span>
                </div>
                <div className="flex justify-between font-semibold text-base pt-1 border-t border-border mt-1">
                  <span>{isFr ? 'Prix total demandé' : 'Total Asking Price'}</span>
                  <span>{fmt(combinedTotal, currency)}</span>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div className="border-t pt-6">
        <h4 className="text-lg font-medium mb-4">{t.step6.revenueSection}</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="revenueYear1">{t.step6.revenueYear1} *</Label>
            <CurrencyInput id="revenueYear1" min={0} value={formData.financials.revenueYear1 || ''} onChange={(v) => handleChange('revenueYear1', v)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="revenueYear2">{t.step6.revenueYear2} *</Label>
            <CurrencyInput id="revenueYear2" min={0} value={formData.financials.revenueYear2 || ''} onChange={(v) => handleChange('revenueYear2', v)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="revenueYear3">{t.step6.revenueYear3} *</Label>
            <CurrencyInput id="revenueYear3" min={0} value={formData.financials.revenueYear3 || ''} onChange={(v) => handleChange('revenueYear3', v)} />
          </div>
        </div>
      </div>

      <div className="border-t pt-6">
        <h4 className="text-lg font-medium mb-4">{t.step6.ebitdaSection}</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="ebitdaYear1">{t.step6.ebitdaYear1} *</Label>
            <CurrencyInput id="ebitdaYear1" value={formData.financials.ebitdaYear1 || ''} onChange={(v) => handleChange('ebitdaYear1', v)} allowNegative />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ebitdaYear2">{t.step6.ebitdaYear2} *</Label>
            <CurrencyInput id="ebitdaYear2" value={formData.financials.ebitdaYear2 || ''} onChange={(v) => handleChange('ebitdaYear2', v)} allowNegative />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ebitdaYear3">{t.step6.ebitdaYear3} *</Label>
            <CurrencyInput id="ebitdaYear3" value={formData.financials.ebitdaYear3 || ''} onChange={(v) => handleChange('ebitdaYear3', v)} allowNegative />
          </div>
        </div>
      </div>

      <div className="border-t pt-6">
        <h4 className="text-lg font-medium mb-4">{t.step6.debtEquitySection}</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="cashFlowEquity">{t.step6.cashFlowEquity} *</Label>
            <CurrencyInput
              id="cashFlowEquity"
              value={formData.financials.cashFlowEquity || ''}
              onChange={(v) => handleChange('cashFlowEquity', v)}
              allowNegative
              className={errors.cashFlowEquity ? 'border-destructive' : ''}
            />
            <p className="text-xs text-amber-600">{t.step6.cashFlowEquityHint}</p>
            {errors.cashFlowEquity && <p className="text-sm text-destructive">{errors.cashFlowEquity}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="totalLiabilities">{t.step6.totalLiabilities} *</Label>
            <CurrencyInput id="totalLiabilities" min={0} value={formData.financials.totalLiabilities || ''} onChange={(v) => handleChange('totalLiabilities', v)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="inheritedDebt">{t.step6.inheritedDebt} *</Label>
            <CurrencyInput id="inheritedDebt" min={0} value={formData.financials.inheritedDebt || ''} onChange={(v) => handleChange('inheritedDebt', v)} />
            <p className="text-xs text-muted-foreground">{t.step6.inheritedDebtHint}</p>
          </div>
        </div>
      </div>
    </div>
  );
};